// Live HTML preview of the "Modern" A6 sticker template.
// Mirrors the PDF layout produced by backend/pdf_generator.py.

function splitQuantity(q) {
  if (!q) return ["", ""];
  const trimmed = q.trim();
  const units = new Set([
    "pcs","pc","piece","pieces","unit","units","ea","each",
    "box","boxes","ctn","kg","g","lb","lbs","oz","ml","l",
    "m","cm","mm","ft","in","pack","packs","set","sets",
    "pair","pairs","roll","rolls","sheet","sheets",
  ]);
  // "500 pcs"
  const m1 = trimmed.match(/^(.*?)(?:\s+)([a-zA-Z./%]{1,10})$/);
  if (m1 && units.has(m1[2].toLowerCase().replace(/\.+$/, ""))) {
    return [m1[1].trim(), m1[2].trim()];
  }
  // "500pcs"
  const m2 = trimmed.match(/^([0-9.,\-+/x× ]+)([a-zA-Z./%]{1,10})$/);
  if (m2 && units.has(m2[2].toLowerCase())) {
    return [m2[1].trim(), m2[2].trim()];
  }
  return [trimmed, ""];
}

function formatDate(dateStr) {
  if (!dateStr) {
    const d = new Date();
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }
  // dateStr is YYYY-MM-DD from <input type="date">
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
  }
  return dateStr;
}

function Placeholder({ text }) {
  return <span className="text-neutral-300">{text}</span>;
}

export default function StickerPreview({
  company,
  itemName,
  quantity,
  description,
  boxes = 1,
  boxIndex = 1,
  date,
}) {
  const totalBoxes = Math.max(1, Number(boxes) || 1);
  const idx = Math.min(Math.max(1, Number(boxIndex) || 1), totalBoxes);
  const showBox = totalBoxes >= 2;
  const [qtyNum, qtyUnit] = splitQuantity(quantity);
  const dateLabel = formatDate(date);

  return (
    <div className="sticker-frame p-3 fade-up" data-testid="sticker-preview">
      <div
        className="sticker-inner relative bg-white"
        style={{
          aspectRatio: "148 / 105",
          padding: "4.5%",
          // Pin the on-screen preview to the exact same family the PDF
          // generator uses (bundled Inter TTFs). System-ui fallback keeps
          // the page sane if the webfont is blocked.
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Top row */}
        <div className="absolute top-[4.5%] left-[4.5%] right-[4.5%] flex items-center justify-between h-[10%]">
          <div className="h-full flex items-center">
            {company?.logo_data_url ? (
              <img
                src={company.logo_data_url}
                alt={company.name}
                className="max-h-full max-w-[140px] object-contain"
              />
            ) : (
              <div
                className="font-bold text-neutral-900 leading-none"
                style={{ fontSize: "clamp(11px, 1.4vw, 14px)" }}
              >
                {company?.name || <Placeholder text="Company" />}
              </div>
            )}
          </div>
          <div
            className="text-neutral-400 font-bold uppercase tracking-wide"
            style={{ fontSize: "clamp(9px, 1vw, 11px)" }}
          >
            {dateLabel}
          </div>
        </div>

        {/* Title + description block.
            Bottom edge sits 6% above the separator (which is at bottom:26%)
            so text never visually touches or crosses the divider line. */}
        <div
          className="absolute left-[4.5%] right-[4.5%] flex flex-col overflow-hidden"
          style={{ top: "16%", bottom: "32%" }}
        >
          <div
            className="font-black text-neutral-900 tracking-tight break-words min-h-0"
            style={{
              // When a description is present, cap title smaller so 3 title
              // lines + 2 description lines still fit. With no description we
              // can use the bigger scale. Max sizes are calibrated so that
              // 3 lines at the chosen line-height fit comfortably within the
              // title block, leaving room for descenders (g, p, y) so the
              // overflow:hidden (required for line-clamp) never clips them.
              fontSize: description
                ? "clamp(13px, 2.4vw, 28px)"
                : "clamp(14px, 3vw, 36px)",
              lineHeight: 1.15,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              // Tiny safety pad so the last line's descenders always have
              // breathing room below the baseline before the clipping edge.
              paddingBottom: "0.05em",
            }}
          >
            {itemName || <Placeholder text="Item name" />}
          </div>
          {description ? (
            <div
              className="mt-1.5 text-neutral-500 leading-tight flex-shrink-0"
              style={{
                fontSize: "clamp(8px, 1vw, 10px)",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {description}
            </div>
          ) : null}
        </div>

        {/* Separator */}
        <div
          className="absolute left-[4.5%] right-[4.5%] border-t border-black"
          style={{ bottom: "26%" }}
        />

        {/* Bottom row */}
        <div
          className="absolute left-[4.5%] right-[4.5%] flex items-end justify-between"
          style={{ bottom: "4.5%", top: "auto", height: "20%" }}
        >
          <div className="flex flex-col justify-end h-full min-w-0">
            {showBox ? (
              <>
                <div
                  className="text-neutral-400 font-bold uppercase tracking-wider"
                  style={{ fontSize: "clamp(9px, 1vw, 11px)" }}
                >
                  Box
                </div>
                <div
                  className="font-black text-neutral-900 leading-none tracking-tight"
                  style={{ fontSize: "clamp(20px, 4vw, 42px)" }}
                >
                  {idx}/{totalBoxes}
                </div>
              </>
            ) : null}
          </div>
          <div className="flex items-end gap-2">
            <div
              className="font-black text-neutral-900 leading-none tracking-tighter text-right"
              style={{ fontSize: "clamp(28px, 6vw, 64px)" }}
            >
              {qtyNum || quantity || <Placeholder text="Quantity" />}
            </div>
            {qtyUnit ? (
              <div
                className="text-neutral-400 font-bold leading-none pb-[3%]"
                style={{ fontSize: "clamp(10px, 1.4vw, 16px)" }}
              >
                {qtyUnit}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
